import React, { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { RESERVED_DOMAINS, RESERVED_DOMAIN_BY_KEY } from '../reservedDomains';
import { MentionText, isInProgress, mentionedProjectIds, oldLabel } from './focus';

const known = (keys) => (Array.isArray(keys) ? keys.filter((key) => RESERVED_DOMAIN_BY_KEY[key]) : []);

export const ReservedDomainChips = ({ keys, className = '' }) => {
  const list = known(keys);
  if (list.length === 0) return null;
  return (
    <span className={`inline-flex flex-wrap gap-1 ${className}`}>
      {list.map((key) => (
        <span
          key={key}
          title={RESERVED_DOMAIN_BY_KEY[key].effect}
          className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700"
        >
          <KeyRound size={11} />
          {RESERVED_DOMAIN_BY_KEY[key].label}
        </span>
      ))}
    </span>
  );
};

/** Où chaque domaine à effet du rôle s'applique : les projets visés par le focus de ses titulaires. */
export const reservedDomainScope = (role, ownerIds, roleFocusMap) => {
  const scope = [];
  (ownerIds || []).forEach((employeeId) => {
    const focus = roleFocusMap?.[`${role.id}:${employeeId}`] || '';
    mentionedProjectIds(focus).forEach((projectId) => scope.push({ projectId, employeeId }));
  });
  return scope;
};

// Projets où s'appliquent les domaines à effet : une ligne de résumé (projets en cours), la liste au clic ; les projets
// terminés du focus seulement à la demande.
const EffectScope = ({ scope: all, projectById, findEmployee, onOpenProject }) => {
  const [open, setOpen] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const current = all.filter((item) => isInProgress(projectById?.get(Number(item.projectId))));
  const old = all.filter((item) => !isInProgress(projectById?.get(Number(item.projectId))));
  const scope = showOld ? [...current, ...old] : current;
  if (all.length === 0) {
    return (
      <p className="text-[11px] text-amber-700">
        Sans effet pour l'instant : aucun titulaire n'a de focus qui vise un projet (ex. « @Projet »).
      </p>
    );
  }
  const projects = new Set(current.map((item) => item.projectId));
  const oldProjects = [...new Set(old.map((item) => item.projectId))];
  const people = [...new Set(current.map((item) => item.employeeId))]
    .map((id) => findEmployee?.(id)?.name?.split(' ')[0])
    .filter(Boolean);
  return (
    <div className="text-[11px] text-slate-500">
      <button type="button" onClick={() => setOpen((value) => !value)} className="hover:text-violet-700">
        S'appliquent à {projects.size} projet{projects.size > 1 ? 's' : ''} en cours
        {people.length > 0 ? ` (focus de ${people.join(', ')})` : ''}
        <span className="ml-1 font-semibold text-violet-700">{open ? 'masquer' : 'voir'}</span>
      </button>
      {open && (
        <span className="mt-1.5 flex flex-wrap gap-1">
          {scope.map(({ projectId, employeeId }) => (
            <button
              key={`${projectId}-${employeeId}`}
              type="button"
              onClick={() => onOpenProject && onOpenProject(projectId)}
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium hover:opacity-80 ${
                isInProgress(projectById?.get(Number(projectId)))
                  ? 'border-emerald-100 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-500'
              }`}
            >
              {projectById?.get(Number(projectId))?.name || `Projet ${projectId}`}
              <span className="ml-1 opacity-70">· {findEmployee?.(employeeId)?.name?.split(' ')[0] || '?'}</span>
            </button>
          ))}
          {oldProjects.length > 0 && (
            <button type="button" onClick={() => setShowOld((value) => !value)}
              className="px-1 text-[11px] font-semibold text-slate-400 hover:text-violet-700">
              {showOld ? 'masquer les autres' : oldLabel(oldProjects.map((id) => projectById?.get(Number(id))))}
            </button>
          )}
        </span>
      )}
    </div>
  );
};

/**
 * Où s'applique le domaine `key` d'un cercle : son leader le tient, sans focus, sur les projets du cercle et de ses
 * sous-cercles, sauf ceux qu'un rôle tient par son focus (délégué). Même règle que reserved_domains.holders.
 */
export const circleDomainScope = (circle, key, circles, projects, roleFocusMap) => {
  const childrenOf = new Map();
  (circles || []).forEach((c) => {
    if (c?.parent_id === null || c?.parent_id === undefined) return;
    childrenOf.set(String(c.parent_id), [...(childrenOf.get(String(c.parent_id)) || []), c]);
  });
  const subtree = new Set();
  const walk = (id) => {
    if (subtree.has(String(id))) return;
    subtree.add(String(id));
    (childrenOf.get(String(id)) || []).forEach((child) => walk(child.id));
  };
  walk(circle.id);
  // Un sous-cercle qui porte lui aussi le domaine le tient pour ses projets (le plus proche l'emporte).
  const byId = new Map((circles || []).map((c) => [String(c.id), c]));
  const nearest = (circleId) => {
    let current = byId.get(String(circleId));
    while (current && String(current.id) !== String(circle.id)) {
      if (known(current.reserved_domains).includes(key) && childrenOf.has(String(current.id))) return current;
      current = byId.get(String(current.parent_id));
    }
    return circle;
  };
  const delegated = new Map(); // projet → rôle qui le tient par son focus
  (circles || []).filter((c) => !childrenOf.has(String(c.id)) && known(c.reserved_domains).includes(key)).forEach((role) => {
    const owners = [role.owner_id, ...(role.owner_ids || [])].filter((v) => v !== null && v !== undefined);
    owners.forEach((employeeId) => {
      mentionedProjectIds(roleFocusMap?.[`${role.id}:${employeeId}`] || '').forEach((pid) => delegated.set(Number(pid), role));
    });
  });
  const mine = (projects || []).filter((p) => p?.circle_id !== null && p?.circle_id !== undefined
    && subtree.has(String(p.circle_id)) && String(nearest(p.circle_id).id) === String(circle.id));
  return {
    held: mine.filter((p) => !delegated.has(Number(p.id))),
    delegated: mine.filter((p) => delegated.has(Number(p.id))).map((p) => ({ project: p, role: delegated.get(Number(p.id)) })),
  };
};

const CircleEffectScope = ({ circle, effects, circles, projects, roleFocusMap, ownerIds, findEmployee, onOpenProject }) => {
  const [open, setOpen] = useState(null);
  const [showOld, setShowOld] = useState(false);
  const leaders = (ownerIds || []).map((id) => findEmployee?.(id)?.name?.split(' ')[0]).filter(Boolean);
  if (!leaders.length) {
    return <p className="text-[11px] text-amber-700">Sans effet pour l'instant : le cercle n'a pas de leader.</p>;
  }
  return (
    <div className="space-y-1">
      {effects.map((key) => {
        const { held, delegated } = circleDomainScope(circle, key, circles, projects, roleFocusMap);
        const current = held.filter(isInProgress);
        const old = held.filter((p) => !isInProgress(p));
        const shown = showOld ? [...current, ...old] : current;
        return (
          <div key={key} className="text-[11px] text-slate-500">
            <button type="button" onClick={() => setOpen(open === key ? null : key)} className="text-left hover:text-violet-700">
              {effects.length > 1 && <span className="font-medium text-slate-600">{RESERVED_DOMAIN_BY_KEY[key].label} : </span>}
              tenu par {leaders.join(', ')} (leader) sur les {current.length} projet{current.length > 1 ? 's' : ''} en cours du
              cercle, sans focus
              {delegated.length > 0 && `, sauf ${delegated.length} confié${delegated.length > 1 ? 's' : ''} à un rôle`}
              <span className="ml-1 font-semibold text-violet-700">{open === key ? 'masquer' : 'voir'}</span>
            </button>
            {open === key && (
              <span className="mt-1.5 flex flex-wrap gap-1">
                {shown.map((project) => (
                  <button key={project.id} type="button" onClick={() => onOpenProject && onOpenProject(project.id)}
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium hover:opacity-80 ${
                      isInProgress(project) ? 'border-emerald-100 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-500'
                    }`}>
                    {project.name}
                  </button>
                ))}
                {delegated.map(({ project, role }) => (
                  <button key={`d-${project.id}`} type="button" onClick={() => onOpenProject && onOpenProject(project.id)}
                    title={`Tenu par le rôle ${role.name} (focus)`}
                    className="inline-flex items-center rounded-full border border-dashed border-violet-200 px-2 py-0.5 text-[11px] text-violet-700 hover:opacity-80">
                    {project.name}<span className="ml-1 opacity-70">· {role.name}</span>
                  </button>
                ))}
                {old.length > 0 && (
                  <button type="button" onClick={() => setShowOld((value) => !value)}
                    className="px-1 text-[11px] font-semibold text-slate-400 hover:text-violet-700">
                    {showOld ? 'masquer les autres' : oldLabel(old)}
                  </button>
                )}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Domaines d'un rôle ou d'un cercle, en une seule liste. Ceux qui ont un effet dans Deepflow (🔑) disent ce qu'ils
 * réservent et sur quels projets (le focus des titulaires) ; les autres sont de simples puces.
 */
export const DomainsList = ({
  circle, ownerIds, roleFocusMap, projectById, findEmployee, onOpenProject, mentionProps = {},
  isCircle = false, circles = [], projects = [],
}) => {
  const effects = known(circle?.reserved_domains);
  const domains = Array.isArray(circle?.domains) ? circle.domains.filter((item) => String(item || '').trim()) : [];
  if (effects.length === 0 && domains.length === 0) return <p className="text-sm text-slate-400">Aucun domaine.</p>;
  return (
    <div className="space-y-2">
      <ul className="space-y-1.5">
        {effects.map((key) => (
          <li key={key} className="flex items-start gap-2 text-sm text-slate-700">
            <KeyRound size={13} className="mt-[3px] shrink-0 text-violet-500" />
            <span className="min-w-0">
              <span className="font-medium text-slate-800">{RESERVED_DOMAIN_BY_KEY[key].label}</span>
              <span className="block text-xs text-slate-500">{RESERVED_DOMAIN_BY_KEY[key].effect}</span>
            </span>
          </li>
        ))}
        {domains.map((item, index) => (
          <li key={`${index}-${String(item).slice(0, 24)}`} className="flex items-start gap-2 text-sm text-slate-700">
            <span className="mt-[7px] w-1.5 h-1.5 rounded-full shrink-0 bg-indigo-500" />
            <span className="min-w-0"><MentionText text={item} {...mentionProps} /></span>
          </li>
        ))}
      </ul>
      {effects.length > 0 && isCircle && (
        <div className="pl-5">
          <CircleEffectScope circle={circle} effects={effects} circles={circles} projects={projects} roleFocusMap={roleFocusMap}
            ownerIds={ownerIds} findEmployee={findEmployee} onOpenProject={onOpenProject} />
        </div>
      )}
      {effects.length > 0 && !isCircle && (
        <div className="pl-5">
          <EffectScope
            scope={reservedDomainScope(circle, ownerIds, roleFocusMap)}
            projectById={projectById}
            findEmployee={findEmployee}
            onOpenProject={onOpenProject}
          />
        </div>
      )}
    </div>
  );
};

/** Domaines à effet à cocher (fiche en super édition), au-dessus des domaines libres. */
export const ReservedDomainPicker = ({ value, onChange }) => {
  const selected = new Set(known(value));
  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(RESERVED_DOMAINS.map((d) => d.key).filter((k) => next.has(k)));
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {RESERVED_DOMAINS.map((domain) => {
        const active = selected.has(domain.key);
        return (
          <label
            key={domain.key}
            title={domain.effect}
            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition ${
              active ? 'border-violet-300 bg-violet-50 text-violet-800' : 'border-slate-200 bg-white text-slate-500 hover:border-violet-200'
            }`}
          >
            <input type="checkbox" checked={active} onChange={() => toggle(domain.key)} className="accent-violet-600" />
            <KeyRound size={11} />
            {domain.label}
          </label>
        );
      })}
    </div>
  );
};
